import React, { useState } from "react";
import { tss } from "tss-react";
import { MenuItem, Typography } from "@mui/material";
import { RasterIcon } from "assets/images";
import { Tooltip, ConfirmationDialog } from "components";
import RasterActions from "../actions/RasterActions";
import { iconColor } from "styles/common";

const useStyles = tss.create((props: any) => ({
    menuItem: {
        display: "inline-flex",
        alignItems: "center",
        height: 40,
        width: "100%",
        boxShadow: "0 1px 0 0 #E5E7F4",
        paddingLeft: 10 + props.folderLevel * 11,
    },
    icon: {
        height: 24,
    },
    main: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        height: 40,
        paddinLeft: 0,
    },
    name: {
        marginLeft: 10,
        color: "#434343",
        fontFamily: "Arial",
        fontSize: 14,
        letterSpacing: 0,
        lineHeight: "16px",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
        width: 250,
    },
    disabled: {
        opacity: 0.25,
    },
    selected: {
        color: "rgb(0, 117, 176)",
        background: "rgba(0,39,118,0.1)",
    },
}));

type IRaster = {
    ID: string;
    index: number;
    layer: any;
    currentProject: any;
    folderLevel: number;
    updateParent: () => void;
    refreshRasters: () => void;
};

export default function RasterImage(props: IRaster) {
    const { classes } = useStyles(props);
    const { ID, index, layer, currentProject, refreshRasters } = props;
    const [update, setUpdate] = useState(false);
    const [openConfirmation, setOpenConfirmation] = useState(false);

    const raster = currentProject[ID];

    var onUpdate = () => {
        //console.log('update.rasterImage');
        setUpdate((prevUpate) => !prevUpate);
    };

    var removeSelection = () => {
        RasterActions.setSelected(null);
    };

    React.useEffect(() => {
        RasterActions.on("update", onUpdate);
        RasterActions.on("closeRaster", removeSelection);
        return () => {
            RasterActions.removeEventListener("update", onUpdate);
            RasterActions.removeEventListener("closeRaster", removeSelection);
        };
    }, []);

    if (!raster) {
        return <div />;
    }

    let transparency = raster.transparency ? Math.round(raster.transparency * 100) : 100;
    let isVisible = raster.visible ? (raster.visible === "1" ? true : false) : true;
    //console.log(ID, transparency, isVisible);
    const handleClick = (e: any) => {
        RasterActions.setSelected(raster);
    };

    const showToolTip = (e: any) => {
        var tooltip = document.getElementById("raster-tooltip-" + index);
        var name = document.getElementById("raster-name-" + index);
        if (tooltip && name) {
            if (e.type === "mouseover") {
                name.style.width = "135px";
                tooltip.style.display = "inline-flex";
            } else {
                name.style.width = "250px";
                tooltip.style.display = "none";
            }
        }
    };
    const isSelected = RasterActions.selected === ID;

    const toggleVisibility = () => {
        var newValue = !isVisible;
        RasterActions.setVisibility(layer, raster, newValue);
    };

    const setTransparency = (value: number) => {
        var newValue = value / 100.0;
        RasterActions.setTransparency(layer, raster, newValue);
    };

    const deleteImage = () => {
        setOpenConfirmation(true);
    };

    const confirmAction = (object: any) => {
        if (object.status) {
            RasterActions.deleteRaster(raster, layer.ID, () => {
                refreshRasters();
            });
            setOpenConfirmation(false);
        } else {
            setOpenConfirmation(false);
        }
    };

    const handleDrag = (evt: any) => {
        RasterActions.draggedRaster = ID;
        evt.preventDefault();
        evt.stopPropagation();
    };

    const handleDrop = (evt: any) => {
        evt.preventDefault();
        var srcRaster = RasterActions.draggedRaster;
        var targetRaster = ID;
        var list = layer["raster_list"].trim().split(" ");
        var ortho = layer["ortho_list"].trim().split(" ");
        var srcIndex: number = list.indexOf(srcRaster);
        var targetIndex: number = list.indexOf(targetRaster);
        list.splice(targetIndex, 0, list.splice(srcIndex, 1)[0]);
        ortho.splice(targetIndex, 0, ortho.splice(srcIndex, 1)[0]);
        layer["raster_list"] = list.join(" ");
        layer["ortho_list"] = ortho.join(" ");
        refreshRasters();
    };

    return (
        <div
            className={[!isVisible ? classes.disabled : "", isSelected ? classes.selected : ""].join(" ")}
            onMouseOver={showToolTip}
            onMouseOut={showToolTip}
            draggable={true}
            onDrag={handleDrag}
            onDrop={handleDrop}
            style={{
                userSelect: update ? "none" : "none",
            }}
        >
            <MenuItem className={classes.menuItem} value={ID}>
                <div className={classes.main} onClick={handleClick}>
                    <div className={classes.icon}>
                        <RasterIcon style={{ color: iconColor, width: 24, height: 24 }} />
                    </div>
                    <Typography id={"raster-name-" + index} className={classes.name}>
                        {raster.name}
                    </Typography>
                </div>
                <Tooltip
                    id={"raster-tooltip-" + index}
                    actions={{
                        setTransparency: setTransparency,
                        toggleVisibility: toggleVisibility,
                        deleteImage: deleteImage,
                    }}
                    values={{ isVisible: isVisible, opacity: transparency }}
                />
            </MenuItem>
            {openConfirmation ? (
                <ConfirmationDialog
                    open={openConfirmation}
                    title="Delete Image"
                    type="image"
                    name={raster.name}
                    confirmAction={confirmAction}
                />
            ) : (
                ""
            )}
        </div>
    );
}
